import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    Phone,
    Navigation,
    Share2,
    Tag,
    Loader2,
    Clock,
    MessageCircle,
    CheckCircle2,
    XCircle,
    Sparkles,
    X,
    ChevronLeft,
    ChevronRight,
    Heart,
    Plus,
    Info,
    Wallet,
    Crown,
} from "lucide-react";
import { loadStoreDetail, closeStoreDetail } from "../../store/storeSlices/storeDetailSlice";
import VoucherCheckoutPanel, {
    QuantityStepper,
    getVoucherPricing,
    getVoucherAvailability,
    getVoucherCoins,
    isVoucherEligible,
    redemptionLabel,
    voucherDiscountText,
    formatINR,
    getRewardCashback,
    cashbackTitle,
} from "./VoucherCheckoutPanel";
import PayBillPanel from "./PayBillPanel";
import OtpVerifyModal from "./modals/OtpVerifyModal";
import LoginRequiredModal from "./modals/LoginRequiredModal";
import MembershipPage, { getProgramRewards, isMemberReward } from "./MembershipPage";
import { getRewardHeadline, getBestHeadline, getVisibleRewards, hasBenefit, hasDiscount as rewardHasDiscount } from "../../utils/rewardUtils";
import { saveCheckoutOrder } from "../../utils/checkoutOrder";
import { sendOtp, verifyOtp, isApiSuccess,addVoucherToCart, updateVoucherCartQty, removeVoucherFromCart,checkoutVoucherCart  } from "../../services/api";
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import { getMembershipStatus, getOwnedProgramIds, getMembershipCredit } from "../../utils/membershipStatus";
import StoreTags from "./StoreTags";
import toastr from "toastr";

const CHECKOUT_PATH = "/web/store-payment";
const LOGIN_PATH = "/login"; // where a logged-out visitor is sent

// Field names are best guesses for the profile object: adjust to your user payload
const getUserPhone = (user) => String(user?.mobile ?? user?.phone ?? "").replace(/\D/g, "").slice(-10);
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function getOpenStatus(timings) {
    if (!timings) return null;
    const now = new Date();
    const today = timings[DAY_KEYS[now.getDay()]];
    if (!today || !today.is_open) return { open: false, label: "Closed today" };

    const [oh, om] = today.open_time.split(":").map(Number);
    const [ch, cm] = today.close_time.split(":").map(Number);
    const openMinutes = oh * 60 + om;
    const closeMinutes = ch * 60 + cm;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const isOpen = nowMinutes >= openMinutes && nowMinutes <= closeMinutes;

    const fmt = (h, m) => {
        const period = h >= 12 ? "PM" : "AM";
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
    };

    return {
        open: isOpen,
        label: `${isOpen ? "Open now" : "Closed"} · ${fmt(oh, om)} – ${fmt(ch, cm)}`,
    };
}

function formatDiscount(item, kind) {
    if (kind === "voucher") return voucherDiscountText(item); // customer's share already applied
    return getRewardHeadline(item).text;
}

/** valid_from / valid_to are optional: when both are missing the voucher has no date restriction */
function getVoucherValidity(v) {
    const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    const from = v.valid_from ? new Date(v.valid_from) : null;
    const to = v.valid_to ? new Date(v.valid_to) : null;
    if (to) to.setHours(23, 59, 59, 999); // valid through the end of the last day
    const now = new Date();

    if (from && now < from) return { usable: false, label: `Starts ${fmt(from)}` };
    if (to && now > to) return { usable: false, label: `Expired on ${fmt(to)}` };
    if (from && to) return { usable: true, label: `Valid ${fmt(from)} – ${fmt(to)}` };
    if (from) return { usable: true, label: `Valid from ${fmt(from)}` };
    if (to) return { usable: true, label: `Valid till ${fmt(to)}` };
    return { usable: true, label: null };
}

/** Fullscreen viewer for a gallery image, with prev/next when there's more than one */
function Lightbox({ images, index, onClose, onPrev, onNext }) {
    useEffect(() => {
        if (index === null) return;
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        };
        window.addEventListener("keydown", handleKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [index, onClose, onPrev, onNext]);

    if (index === null) return null;
    const img = images[index];

    return (
        <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                aria-label="Close"
            >
                <X size={20} />
            </button>

            {images.length > 1 && (
                <span className="absolute top-4 left-4 text-white/80 text-sm font-medium tabular-nums">
                    {index + 1} / {images.length}
                </span>
            )}

            {images.length > 1 && (
                <>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        className="absolute left-2 sm:left-4 p-2 sm:p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                        aria-label="Previous image"
                    >
                        <ChevronLeft size={22} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        className="absolute right-2 sm:right-4 p-2 sm:p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                        aria-label="Next image"
                    >
                        <ChevronRight size={22} />
                    </button>
                </>
            )}

            <img
                src={img.image_path}
                alt=""
                onClick={(e) => e.stopPropagation()}
                className="max-w-[92vw] max-h-[85vh] object-contain rounded-lg select-none"
            />
        </div>
    );
}

/** Adaptive Collage Grid supporting 0-5 images; click any tile to open it fullscreen */
function ImageGallery({ images }) {
    const [openIndex, setOpenIndex] = useState(null);
    const [isFavorite, setIsFavorite] = useState(false);

    if (!images || images.length === 0) {
        return (
            <div className="h-48 sm:h-64 bg-gradient-to-br from-gray-100 to-gray-200 w-full flex flex-col items-center justify-center text-gray-400 gap-2">
                <Sparkles size={24} className="text-gray-300" />
                <span className="text-xs font-medium">No store photos available</span>
            </div>
        );
    }

    const items = images.slice(0, 5);
    const count = items.length;

    const handlePrev = () => setOpenIndex((i) => (i - 1 + count) % count);
    const handleNext = () => setOpenIndex((i) => (i + 1) % count);

    // Each tile clips its own image, so the hover zoom never bleeds into neighboring tiles
    const Tile = ({ img, idx, className = "" }) => (
        <button
            type="button"
            onClick={() => setOpenIndex(idx)}
            className={`relative block overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B67FC]/60 focus-visible:ring-inset ${className}`}
            aria-label={`View photo ${idx + 1}`}
        >
            <img
                src={img.image_path}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 group-active:scale-100 transition-transform duration-300"
            />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </button>
    );

    // Heart / wishlist toggle shown over the main tile, matching the reference layout
    const FavoriteButton = (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                setIsFavorite((f) => !f);
            }}
            className="absolute bottom-3 right-3 z-10 p-2.5 rounded-full bg-black/40 hover:bg-black/55 backdrop-blur-sm text-white transition-colors"
            aria-label={isFavorite ? "Remove from wishlist" : "Add to wishlist"}
        >
            <Heart size={18} className={isFavorite ? "fill-red-500 text-red-500" : ""} />
        </button>
    );

    let grid;
    if (count === 1) {
        grid = (
            <div className="relative h-full">
                <Tile img={items[0]} idx={0} className="w-full h-full" />
                {FavoriteButton}
            </div>
        );
    } else if (count === 2) {
        grid = (
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2 h-full">
                <div className="relative h-full">
                    <Tile img={items[0]} idx={0} className="h-full" />
                    {FavoriteButton}
                </div>
                <Tile img={items[1]} idx={1} className="h-full" />
            </div>
        );
    } else if (count === 3) {
        grid = (
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 h-full">
                <div className="col-span-2 relative h-full">
                    <Tile img={items[0]} idx={0} className="h-full" />
                    {FavoriteButton}
                </div>
                <div className="col-span-1 grid grid-rows-2 gap-1.5 sm:gap-2 h-full">
                    <Tile img={items[1]} idx={1} className="h-full" />
                    <Tile img={items[2]} idx={2} className="h-full" />
                </div>
            </div>
        );
    } else if (count === 4) {
        grid = (
            <div className="grid grid-cols-12 gap-1.5 sm:gap-2 h-full">
                <div className="col-span-7 sm:col-span-8 relative h-full">
                    <Tile img={items[0]} idx={0} className="h-full" />
                    {FavoriteButton}
                </div>
                <div className="col-span-5 sm:col-span-4 grid grid-rows-3 gap-1.5 sm:gap-2 h-full">
                    {items.slice(1).map((img, i) => (
                        <Tile key={img.id} img={img} idx={i + 1} className="h-full" />
                    ))}
                </div>
            </div>
        );
    } else {
        grid = (
            <div className="grid grid-cols-12 gap-1.5 sm:gap-2 h-full">
                <div className="col-span-6 relative h-full">
                    <Tile img={items[0]} idx={0} className="h-full" />
                    {FavoriteButton}
                </div>
                <div className="col-span-6 grid grid-cols-2 grid-rows-2 gap-1.5 sm:gap-2 h-full">
                    {items.slice(1, 5).map((img, i) => (
                        <Tile key={img.id} img={img} idx={i + 1} className="h-full" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="h-64 sm:h-80 md:h-[420px] w-full overflow-hidden bg-white">{grid}</div>
            <Lightbox
                images={items}
                index={openIndex}
                onClose={() => setOpenIndex(null)}
                onPrev={handlePrev}
                onNext={handleNext}
            />
        </>
    );
}

const TABS = ["Overview", "Offers"];

const MAX_VOUCHER_QTY = 10;

const VOUCHER_STEPS = [
    "Buy a voucher on GoerOne",
    "Shop for select items at the store",
    "Pay with vouchers at store checkout",
];

export default function StoreDetailPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const storeId = searchParams.get("storeId");

    const { store, rewards = [], vouchers = [], loading, error } = useSelector((s) => s.storeDetail);
    const [activeTab, setActiveTab] = useState("Overview");
    const [cart, setCart] = useState({}); // { [voucherId]: quantity }
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [payBillOpen, setPayBillOpen] = useState(false);
    const [membershipOpen, setMembershipOpen] = useState(false);
    const [selectedMembership, setSelectedMembership] = useState(null); // { program, action: "use" | "join" | "buy" }
    const [afterMembership, setAfterMembership] = useState("payBill"); // which panel opens once a plan is chosen
    
    const [loginOpen, setLoginOpen] = useState(false); // "log in first" prompt for OTP stores
    const [otpOpen, setOtpOpen] = useState(false);
    const [pendingPay, setPendingPay] = useState(null); // pay-bill values waiting for the OTP
    const [openTerms, setOpenTerms] = useState({}); // { [voucherId]: true } while that voucher's T&C is expanded
    const [cartIssues, setCartIssues] = useState({});   // { [voucherId]: "Only 2 left" }
    const [canCheckout, setCanCheckout] = useState(true);
    const [cartError, setCartError] = useState("");
    const [serverCartItems, setServerCartItems] = useState([])

    const overviewRef = useRef(null);
    const offersRef = useRef(null);
    const tabNavRef = useRef(null);
    const isClickScrolling = useRef(false);

    const { user,refreshUser  } = useGoerOneAuth();
    useEffect(() => { refreshUser(); }, [refreshUser]);
    const membershipStatus = getMembershipStatus(user);
    const membershipCredit = getMembershipCredit(user); // unused value of the active plan, taken off an upgrade

    const ownedProgramIds = getOwnedProgramIds(user); // active (non-expired) memberships only
    const { visibleRewards, isMember } = getVisibleRewards(rewards, ownedProgramIds);
    const bestHeadline = getBestHeadline(visibleRewards);

    const SECTION_REFS = { Overview: overviewRef, Offers: offersRef };

    const scrollToTab = (tab) => {
        setActiveTab(tab);
        const el = SECTION_REFS[tab]?.current;
        if (!el) return;
        isClickScrolling.current = true;
        const navHeight = tabNavRef.current?.offsetHeight || 0;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({ top, behavior: "smooth" });
        // Release the scrollspy lock once the smooth scroll has settled
        window.clearTimeout(scrollToTab._t);
        scrollToTab._t = window.setTimeout(() => {
            isClickScrolling.current = false;
        }, 600);
    };

    // Skip the membership picker entirely when the user already has a valid
    // (non-expired) membership - "use" it straight away, same as if they'd
    // picked it from the picker themselves.
    useEffect(() => {
        if (selectedMembership) return;
        if (membershipStatus.state !== "active" || !membershipStatus.programId) return;
        setSelectedMembership({
            program: {
                program_id: membershipStatus.programId,
                program_name: membershipStatus.programName,
                membership_amount: 0,
                max_coin_usage_percent: membershipStatus.program?.max_coin_usage_percent,
            },
            action: "use",
        });
    }, [selectedMembership, membershipStatus.state, membershipStatus.programId, membershipStatus.programName]);

    // Scrollspy: as the user scrolls past Overview onto Offers (and back), keep the tab highlight in sync
    useEffect(() => {
        if (!store) return;
        const handleScroll = () => {
            if (isClickScrolling.current) return;
            const navHeight = tabNavRef.current?.offsetHeight || 0;
            const probe = navHeight + 24;
            let current = "Overview";
            for (const tab of TABS) {
                const el = SECTION_REFS[tab]?.current;
                if (el && el.getBoundingClientRect().top - probe <= 0) {
                    current = tab;
                }
            }
            setActiveTab((prev) => (prev === current ? prev : current));
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [store]);

    useEffect(() => {
        if (storeId) {
            dispatch(loadStoreDetail({ storeId }));
            setActiveTab("Overview");
            setCart({});
            setCartIssues({});
            setCanCheckout(true);
            setCheckoutOpen(false);
            setPayBillOpen(false);
        }
    }, [storeId, dispatch]);

    useEffect(() => () => dispatch(closeStoreDetail()), [dispatch]);

    // Logged in while the login prompt is open (in-page login) -> carry on to the OTP step
    useEffect(() => {
        if (loginOpen && user?.id) {
            setLoginOpen(false);
            setOtpOpen(true);
        }
    }, [loginOpen, user?.id]);

    const handleBack = () => navigate(-1);

    if (!storeId) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-gray-500 bg-gray-50">
                No store selected.
            </div>
        );
    }

    if (loading && !store) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500 gap-2 font-medium text-sm">
                <Loader2 size={18} className="animate-spin text-[#0B67FC]" /> Loading store details...
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center">
                <p className="text-sm font-medium text-red-600 mb-4">{error}</p>
                <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700 transition-colors"
                >
                    <ArrowLeft size={16} /> Go Back
                </button>
            </div>
        );
    }

    if (!store) return null;

    const categories = (() => {
        try {
            const parsed = typeof store.product_types === "string" ? JSON.parse(store.product_types) : store.product_types;
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    })();

    const timings = (() => {
        try {
            return typeof store.store_timings === "string" ? JSON.parse(store.store_timings) : store.store_timings;
        } catch {
            return null;
        }
    })();

    const status = getOpenStatus(timings);

    // Pay Bill discount: the chosen membership's rewards (a membership is mandatory before buying)
   const payRewards = selectedMembership
    ? getProgramRewards(rewards, selectedMembership.program)
    : visibleRewards;
    // Membership added to every purchase: only a "buy" costs money, it is charged together with the bill / vouchers
    const upgradeFee = (sel) => {
        const full = ["buy", "renew", "upgrade"].includes(sel.action) ? Number(sel.program.membership_amount) || 0 : 0;
        const credit = sel.action === "upgrade" ? Math.min(membershipCredit.amount, full) : 0;
        return { full, credit, net: Math.round((full - credit) * 100) / 100 };
    };
    const toOrderMembership = (sel) =>
        sel
            ? {
                membershipId: sel.program.program_id,
                name: sel.program.program_name,
                owned: sel.action === "use",
                action: sel.action,
                // fee = what is actually charged. An upgrade gets the unused value of the old plan taken off.
                fee: upgradeFee(sel).net,
                fullFee: upgradeFee(sel).full,
                credit: upgradeFee(sel).credit,
                creditDays: upgradeFee(sel).credit > 0 ? membershipCredit.daysLeft : 0,
                maxCoinPercent: sel.program.max_coin_usage_percent ?? 100,
              }
            : null;
    const membership = toOrderMembership(selectedMembership);

    // Membership the customer is buying with: the one picked here, else the active one they already own
    const activeProgramId = selectedMembership?.program?.program_id ?? ownedProgramIds[0] ?? null;

    // Reward belongs to a different membership than the customer's -> locked, shows an upgrade prompt.
    // No membership yet (activeProgramId null) or reward open to all (program_id null) -> not locked.
    const isRewardLocked = (r) =>
        activeProgramId != null && r.program_id != null && Number(r.program_id) !== Number(activeProgramId);

    // A reward copied to other memberships keeps the original's id in parent_id (original has parent_id 0).
    // Copies of one reward share the same group: show it once - the copy matching the customer's membership,
    // else just the first copy.
    const rewardGroupKey = (r) => (Number(r.parent_id) > 0 ? Number(r.parent_id) : Number(r.reward_id));
    const dedupeRewards = (list) => {
        const byGroup = new Map();
        const matches = (x) => activeProgramId != null && Number(x.program_id) === Number(activeProgramId);
        list.forEach((r) => {
            const key = rewardGroupKey(r);
            const existing = byGroup.get(key);
            if (!existing || (!matches(existing) && matches(r))) byGroup.set(key, r);
        });
        return Array.from(byGroup.values());
    };

    // Show every reward: the customer's own first, other memberships' rewards after them
    const allRewards = dedupeRewards(rewards.filter(hasBenefit));
    const offerRewards = [
        ...allRewards.filter((r) => !isRewardLocked(r)),
        ...allRewards.filter(isRewardLocked),
    ];
    const offerCount = offerRewards.length + vouchers.length;

    const handleShare = () => {
        const url = window.location.href;
        if (navigator.share) {
            navigator.share({ title: store.store_name, url }).catch(() => {});
        } else {
            navigator.clipboard.writeText(url);
        }
    };

    const handleEnquire = () => {
        if (store.phone) {
            window.open(`https://wa.me/${store.phone.replace(/\D/g, "")}`, "_blank");
        }
    };

    // A membership is mandatory to buy: the full-page picker opens first unless one is already chosen
    const openAfterMembership = (target) => {
        if (target === "none") return; // membership picked from a voucher card: just come back to the list
        return target === "checkout" ? setCheckoutOpen(true) : setPayBillOpen(true);
    };

    const membershipRequired = membershipStatus.state !== "active";

    const requireMembership = (target) => {
        if (!membershipRequired) return openAfterMembership(target); // this store doesn't need a membership
        if (selectedMembership) return openAfterMembership(target);
        setAfterMembership(target);
        setMembershipOpen(true);
    };

    const handlePayBill = () => requireMembership("payBill");
    // Vouchers: a membership can NOT be bundled with a voucher order. Without an active
    // membership the user buys the membership on its own first (own payment), then returns
    // to the store and buys the voucher(s) with it.
    const handleProceed = () => {
        if (membershipStatus.state === "active") return setCheckoutOpen(true);
        toastr.info("You need an active membership to buy vouchers. Buy a membership first, then come back to your cart.");
        setAfterMembership("voucherBuy");
        setMembershipOpen(true);
    };

    // "Change" link inside the pay-bill / checkout panels
    const changeMembership = (target) => {
        setAfterMembership(target);
        setMembershipOpen(true);
    };

    const handleMembershipSelect = async (program, action) => {
        // Voucher flow: the membership is purchased on its own (membership-only order -> payment page),
        // and the user returns to this store afterwards. Nothing is added to the voucher total.
        if (afterMembership === "voucherBuy") {
            setMembershipOpen(false);
            if (action === "use") return; // already has it: just back to the list
            return goToCheckout({
                membership: toOrderMembership({ program, action }),
                successPath: `/web/store-details?storeId=${storeId}`, // back to the vouchers / cart
            });
        }

        // Nothing is charged here: a "buy" fee is added to the bill / voucher total and paid together with it.
        // TODO: once the ownership check exists, preload selectedMembership with the user's plan so this page is skipped
        setSelectedMembership({ program, action });
        setMembershipOpen(false);

        // Vouchers that don't belong to the newly chosen membership can't stay in the cart
        const kept = {};
        Object.entries(cart).forEach(([id, q]) => {
            const v = vouchers.find((x) => String(x.id) === id);
            if (!v || isVoucherEligible(v, program.program_id)) kept[id] = q;
            else if (user?.id) removeVoucherFromCart(id).catch((e) => console.error("Cart cleanup failed", e))
        });
        setCart(kept);
        const cartEmpty = Object.keys(kept).length === 0;
        openAfterMembership(afterMembership === "checkout" && cartEmpty ? "none" : afterMembership);
    };

    // // Membership the user is buying with: the one picked here, else the active one they already own
    // const activeProgramId = selectedMembership?.program?.program_id ?? ownedProgramIds[0] ?? null;

    // "Upgrade membership to get this voucher" on a voucher card
    const handleUpgradeForVoucher = () => {
        setAfterMembership("voucherBuy");
        setMembershipOpen(true);
    };

    // Invite-only plans
    const handleRequestConsideration = async (program) => {
        console.log({ program_id: program.program_id, store_id: storeId })
        // TODO: call the invitation-request API with { program_id: program.program_id, store_id: storeId }
    };

    const handleHaveInvitation = async (program) => {
         console.log({ program_id: program.program_id, store_id: storeId })
        // TODO: ask for the invitation code, verify it, then continue like a normal selection: handleMembershipSelect(program, action)
    };

    // Mirrors a cart change to the server. Skipped for logged-out visitors (cart stays local).
    const syncVoucherCart = async (voucherId, prevQty, nextQty) => {
    if (!user?.id) return null;
    let res;
    if (nextQty === 0) res = await removeVoucherFromCart(voucherId);             // DELETE
    else if (prevQty === 0) res = await addVoucherToCart(voucherId, nextQty);    // first add -> POST
    else res = await updateVoucherCartQty(voucherId, nextQty);                   // later change -> PUT

    // the API can answer 200 with { status: false, message: "Only 0 left for ..." }
    if (res?.status === false || res?.status?.success === false) throw res;
    return res;
    };
    // Put one voucher's quantity back if its API call failed
    const restoreQty = (voucherId, qty) =>
        setCart((c) => {
            const n = { ...c };
            if (qty > 0) n[voucherId] = qty;
            else delete n[voucherId];
            return n;
        });

        // Server cart (POST / PUT response `data`) is the source of truth: quantities, issues, can_checkout
        const applyServerCart = (data) => {
            if (!Array.isArray(data?.items)) return;
            const next = {};
            const issues = {};
            data.items.forEach((i) => {
                const qty = Number(i.quantity) || 0;
                if (qty > 0) next[i.voucher_id] = qty;
                if (i.issue) issues[i.voucher_id] = i.issue;
            });
            setCart(next);
            setCartIssues(issues);
            setCanCheckout(data.can_checkout !== false);
            setServerCartItems(data.items);
        };

    // Vouchers: same voucher can be added many times, each add just bumps its quantity
    const changeQty = async (voucherId, delta) => {
    const v = vouchers.find((x) => x.id === voucherId);
    const limit = v ? Math.min(MAX_VOUCHER_QTY, getVoucherAvailability(v).remaining) : MAX_VOUCHER_QTY;
    if (delta > 0 && v && (!isVoucherEligible(v, activeProgramId) || limit === 0)) return;

    const prevQty = cart[voucherId] || 0;
    const next = Math.min(limit, Math.max(0, prevQty + delta));
    if (next === prevQty) return;

    // Update the UI right away, then sync; roll back this voucher if the API fails
    const updated = { ...cart };
    if (next > 0) updated[voucherId] = next;
    else delete updated[voucherId];
    setCart(updated);
    if (Object.keys(updated).length === 0) setCheckoutOpen(false);

    try {
       const res = await syncVoucherCart(voucherId, prevQty, next);
       applyServerCart(res?.data);
    } catch (err) {
    // at the start of changeQty / removeVoucher
    setCartError("");

    // in the catch
    const msg = err?.message || "Unable to update your cart. Please try again.";
    setCartError(msg);
    toastr.error(msg);
    restoreQty(voucherId, prevQty);
    }
};

const removeVoucher = async (voucherId) => {
    const prevQty = cart[voucherId] || 0;
    const updated = { ...cart };
    delete updated[voucherId];
    setCart(updated);
    if (Object.keys(updated).length === 0) setCheckoutOpen(false);

    try {
      const res =  await syncVoucherCart(voucherId, prevQty, 0); // DELETE
      applyServerCart(res?.data);
    } catch (err) {
    // at the start of changeQty / removeVoucher
     setCartError("");

     // in the catch
     const msg = err?.message || "Unable to update your cart. Please try again.";
     setCartError(msg);
     toastr.error(msg);
     restoreQty(voucherId, prevQty);
    }
};

    // Every voucher in the cart. Ones not in this store's list (added earlier) are built from the cart API response.
const cartItems = Object.entries(cart)
    .map(([id, qty]) => {
        const local = vouchers.find((v) => String(v.id) === id);
        if (local) return { voucher: local, qty };
        const s = serverCartItems.find((i) => String(i.voucher_id) === id);
        if (!s) return null;
        return {
            voucher: {
                id: s.voucher_id,
                voucher_name: s.voucher_name,
                voucher_price: Number(s.unit_price) || 0,
                discount_value: 0,
                _external: true, // not part of this store's voucher list
            },
            qty,
        };
    })
    .filter(Boolean);
    const cartCount = cartItems.reduce((sum, { qty }) => sum + qty, 0);
    const cartTotal = cartItems.reduce((sum, { voucher, qty }) => sum + getVoucherPricing(voucher).payable * qty, 0);

    // const handleVoucherPay = async ({ items, total, membershipFee }) => {
    //     console.log({ items, total, membershipFee,storeId:storeId,membershipId:membership.membershipId  })
    //     // TODO: call the voucher purchase API with { store_id: storeId, items, total, membership: { program_id, action, fee: membershipFee } }
    //     // (total already includes membershipFee; a "buy" membership is purchased in the same call)
    // };

    // const handlePayBillSubmit = async ({ amount, discount, payable, cashback, rewardId, membershipFee, total }) => {
    //     console.log({ amount, discount, payable, cashback, membershipFee, total,membershipId:membership.membershipId,rewardId: rewardId,storeId:storeId })
    //     // TODO: call the pay-bill API with { store_id: storeId, amount, discount, payable, cashback, total, membership: { program_id, action, fee: membershipFee } }
    //     // (a "buy" membership is purchased in the same call)
    // };
    const goToCheckout = (parts) => {
    const order = {
        store: { id: storeId, name: store.store_name, address: store.address, cityName: store.cityName, logo: store.logo },
        successPath: `/web/store-details?storeId=${storeId}`,
        ...parts,
    };
    saveCheckoutOrder(order);
    navigate(CHECKOUT_PATH, { state: { order } });
    };

    const handleVoucherPay = async ({ gift = null } = {}) => {
    if (membershipStatus.state !== "active") return handleProceed(); // membership must be bought separately first
    if (!canCheckout) {
        toastr.error("Some vouchers in your cart have issues. Please fix them to continue.");
        return;
    }
    if (!user?.id) return handleLogin(); // the cart/checkout APIs need a logged-in user
    try {
        const res = await checkoutVoucherCart(gift); // gift = null | { name, mobile }
        if (!isApiSuccess(res) || !res?.order) {
            throw new Error(res?.message || "Unable to place voucher order.");
        }
        goToCheckout({
            membership,
            phone: getUserPhone(user),
            vouchers: cartItems.map(({ voucher, qty }) => {
                const p = getVoucherPricing(voucher);
                return { voucher_id: voucher.id, name: voucher.voucher_name, qty, face: p.face, discount: p.discount, payable: p.payable, gtcoin: getVoucherCoins(voucher) };
            }),
            gift, // { name, mobile } | null -> payment page + purchase payload
            voucherOrder: res.order,
            voucherPayment: res.payment,                       // server order, shown on the payment page
            paymentRequired: res.payment_required !== false,
        });
    } catch (err) {
        toastr.error(err?.message || "Unable to place voucher order. Please try again.");
    }
};

    // const handleVoucherPay = async () =>
    //     goToCheckout({
    //         membership,
    //         phone: getUserPhone(user),
    //         vouchers: cartItems.map(({ voucher, qty }) => {
    //             const p = getVoucherPricing(voucher);
    //             return { voucher_id: voucher.id, name: voucher.voucher_name, qty, face: p.face, discount: p.discount, payable: p.payable, gtcoin: getVoucherCoins(voucher) };
    //         }),
    //     });

    // Stores with OTPAllowed = 1 need an OTP (sent to the customer's own number) before paying
    const otpRequired = Number(store?.OTPAllowed) === 1;
    const customerPhone = getUserPhone(user);

    const buildPayBillOrder = ({ amount, discount, cashback, rewardId }, otp) => ({
        membership,
        phone: customerPhone,
        ...(otp ? { otp } : {}),
        paybill: {
            amount, discount, cashback, rewardId,
            rewardName: payRewards.find((r) => r.reward_id === rewardId)?.reward_name || "",
        },
    });

    const handlePayBillSubmit = async (values) => {
        if (!otpRequired) return goToCheckout(buildPayBillOrder(values));
        setPendingPay(values);
        if (!user?.id) return setLoginOpen(true); // login first, then the OTP modal opens
        setOtpOpen(true);
    };

   const sendPaymentOtp = async (phone) => {
    const res = await sendOtp(phone);
    if (!isApiSuccess(res)) throw new Error(res?.message || "Unable to send OTP. Please try again.");
    return res;
};

const handleOtpSubmit = async (otp) => {
    const res = await verifyOtp(customerPhone, otp);
    if (!isApiSuccess(res)) throw new Error(res?.message || "Invalid OTP or mobile number");
    setOtpOpen(false);
    goToCheckout(buildPayBillOrder(pendingPay, otp)); // → payment page, as before
};

    const handleLogin = () => {
        setLoginOpen(false);
        navigate(LOGIN_PATH, { state: { from: `${window.location.pathname}${window.location.search}` } });
    };

    return (
        <div className={`min-h-screen bg-gray-50/60 antialiased ${cartCount > 0 ? "pb-28" : "pb-12"}`}>
            {/* Header & Image Collage Container */}
            <div className="relative max-w-4xl mx-auto bg-white">
                <ImageGallery images={store.images} />

                {/* Floating Top Controls */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10 pointer-events-none">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="pointer-events-auto p-2.5 rounded-full bg-white/80 backdrop-blur-md hover:bg-white text-gray-800 shadow-md transition-all active:scale-95"
                        aria-label="Back"
                    >
                        <ArrowLeft size={18} />
                    </button>

                    <button
                        type="button"
                        onClick={handleShare}
                        className="pointer-events-auto p-2.5 rounded-full bg-white/80 backdrop-blur-md hover:bg-white text-gray-800 shadow-md transition-all active:scale-95"
                        aria-label="Share"
                    >
                        <Share2 size={18} />
                    </button>
                </div>
            </div>

            {/* Store Body Section */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                {/* Store Header Card */}
                <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-5 -mt-8 z-20">
                    <div className="flex items-start gap-4">
                        {store.store_logo ? (
                            <img
                                src={store.store_logo}
                                alt={store.store_name}
                                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border-2 border-white shadow-md bg-white shrink-0 -mt-10 sm:-mt-12"
                            />
                        ) : (
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 border-white shadow-md bg-[#0B67FC] flex items-center justify-center text-white font-bold text-2xl shrink-0 -mt-10 sm:-mt-12">
                                {store.store_name?.[0] || "S"}
                            </div>
                        )}

                        <div className="min-w-0 flex-1">
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight leading-snug">
                                {store.store_name}
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed line-clamp-2">
                                {store.address}{store.cityName ? `, ${store.cityName}` : ""}
                            </p>
                             <StoreTags store={store} className="mt-2" />
                        </div>
                    </div>

                    {/* Status & Category Pills */}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
                        {status && (
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                status.open ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60" : "bg-rose-50 text-rose-600 border border-rose-200/60"
                            }`}>
                                <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`} />
                                <Clock size={12} className="opacity-70" />
                                {status.label}
                            </div>
                        )}

                        {categories.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {categories.map((c) => (
                                    <span key={c} className="px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-md">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Primary & Secondary Action Callouts */}
                    <div className="flex flex-col gap-2.5 mt-5">
                        {store.payment_enabled && (
                            <button
                                type="button"
                                onClick={handlePayBill}
                                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#0B67FC] hover:bg-[#0954D0] text-white font-semibold text-sm shadow-sm transition-all active:scale-[0.99]"
                            >
                                <Sparkles size={16} />
                                Pay Bill
                                {bestHeadline
                                    ? ` · Save ${bestHeadline.isFixed ? formatINR(bestHeadline.amount) : `${bestHeadline.pct}%`}`
                                    : ""}
                            </button>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {store.enquiry_enabled && (
                                <button
                                    type="button"
                                    onClick={handleEnquire}
                                    className="col-span-2 sm:col-span-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold transition-colors shadow-sm"
                                >
                                    <MessageCircle size={16} />
                                    Enquire Now
                                </button>
                            )}

                            {store.phone && (
                                <a
                                    href={`tel:${store.phone}`}
                                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-2xs"
                                >
                                    <Phone size={15} className="text-gray-500" /> Call
                                </a>
                            )}

                            {store.lat && store.lon && (
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lon}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-2xs"
                                >
                                    <Navigation size={15} className="text-gray-500" /> Directions
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div
                    ref={tabNavRef}
                    className="sticky top-0 z-30 flex items-center gap-8 mt-6 border-b border-gray-200/80 px-2 bg-gray-50/60 backdrop-blur-sm"
                >
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => scrollToTab(tab)}
                            className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-all relative ${
                                activeTab === tab
                                    ? "text-[#0B67FC] border-[#0B67FC]"
                                    : "text-gray-500 border-transparent hover:text-gray-700"
                            }`}
                        >
                            {tab}
                            {tab === "Offers" && offerCount > 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-[#0B67FC]/10 text-[#083F94] rounded-full">
                                    {offerCount}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Sections render in sequence so scrolling past Overview naturally continues into Offers */}
                <div className="py-5 space-y-8">
                    <div ref={overviewRef} className="space-y-4 text-sm text-gray-600 scroll-mt-24">
                        <h2 className="text-base font-bold text-gray-900 px-1">Overview</h2>
                        {store.description && (
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-2xs">
                                <h3 className="text-xs font-semibold uppercase text-gray-400 tracking-wider mb-2">About</h3>
                                <p className="leading-relaxed text-gray-700">{store.description}</p>
                            </div>
                        )}

                        {store.contact_name && (
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-2xs flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase text-gray-400 tracking-wider">Contact Person</span>
                                <span className="font-medium text-gray-800">{store.contact_name}</span>
                            </div>
                        )}

                        {timings && (
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-2xs">
                                <h3 className="text-xs font-semibold uppercase text-gray-400 tracking-wider mb-3">Operating Hours</h3>
                                <div className="grid grid-cols-1 divide-y divide-gray-50">
                                    {DAY_KEYS.map((day) => {
                                        const d = timings[day];
                                        const isToday = DAY_KEYS[new Date().getDay()] === day;
                                        return (
                                            <div key={day} className={`flex justify-between py-2 text-xs ${isToday ? "font-semibold text-[#0B67FC]" : "text-gray-600"}`}>
                                                <span className="capitalize w-16">{day}</span>
                                                <span>
                                                    {d?.is_open ? `${d.open_time} – ${d.close_time}` : "Closed"}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div ref={offersRef} className="space-y-3 scroll-mt-24">
                        <h2 className="text-base font-bold text-gray-900 px-1 flex items-center gap-2">
                            Offers For You
                            {offerCount > 0 && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-[#0B67FC]/10 text-[#083F94] rounded-full">
                                    {offerCount}
                                </span>
                            )}
                        </h2>

                        {offerCount === 0 ? (
                            <div className="bg-white p-8 rounded-2xl border border-gray-100 text-center">
                                <Tag className="mx-auto text-gray-300 mb-2" size={28} />
                                <p className="text-sm font-medium text-gray-500">No active offers or vouchers right now.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {offerRewards.length > 0 && <h3 className="text-sm font-bold text-gray-900 px-1">Rewards</h3>}
                                {offerRewards.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {offerRewards.map((r) => {
                                            const cashback = getRewardCashback(r);
                                            const hasDiscount = rewardHasDiscount(r);
                                            const cashbackOnly = cashback && !hasDiscount;

                                            const h = getRewardHeadline(r);
                                             const locked = isRewardLocked(r);
                                                
                                            return (
                                                <div key={`reward-${r.reward_id}`} className={`relative flex h-full flex-col p-3 rounded-xl shadow-2xs border ${
                                                    locked
                                                        ? "bg-amber-50/50 border-amber-200/80"
                                                        : "bg-[#0B67FC]/5 border-[#0B67FC]/20"
                                                }`}>
                                                    <div className="flex items-center gap-1.5 text-[#062E6C]">
                                                        {cashbackOnly ? (
                                                            <Wallet size={14} className="text-[#0B67FC] shrink-0" />
                                                        ) : (
                                                            <Tag size={14} className="text-[#0B67FC] shrink-0" />
                                                        )}
                                                        <span className="text-base font-extrabold leading-tight">
                                                            {cashbackOnly ? cashbackTitle(cashback) : formatDiscount(r, "reward")}
                                                        </span>
                                                    </div>
                                                    <p className="mt-1.5 text-sm font-medium leading-snug text-gray-800 line-clamp-2">{r.reward_name}</p>
                                                    {cashback && hasDiscount && (
                                                        <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-emerald-700">
                                                            <Wallet size={12} className="shrink-0" /> + {cashbackTitle(cashback)}
                                                        </p>
                                                    )}
                                                    {r.ordervalue > 0 && (
                                                        <p className="text-xs text-[#0954D0]/80 mt-1.5">Valid on orders above ₹{r.ordervalue}</p>
                                                    )}
                                                    {!h.isFixed && h.cap > 0 && (
                                                        <p className="mt-1 text-xs text-[#0954D0]/80">
                                                            Max you save {formatINR(h.cap)}
                                                        </p>
                                                    )}
                                                    {cashback?.type === "percent" && cashback.max > 0 && (
                                                        <p className="text-xs text-[#0954D0]/80 mt-1">Max cashback ₹{cashback.max}</p>
                                                    )}
                                                    {r.program_name && (
                                                        <span className="mt-2 self-start text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-[#0B67FC]/20 text-[#083F94] rounded">
                                                            {r.program_name} Members
                                                        </span>
                                                    )}
                                                    {locked && (
                                                        <div className="mt-auto pt-3">
                                                            <button
                                                                type="button"
                                                                onClick={handleUpgradeForVoucher}
                                                                className="inline-flex w-full items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-amber-400 bg-amber-50 text-xs font-bold leading-tight text-amber-800 hover:bg-amber-100 transition-colors"
                                                            >
                                                                <Crown size={14} className="shrink-0" /> Upgrade membership to get this reward
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {vouchers.length > 0 && (
                                    <div className="px-1 pt-4">
                                        <h3 className="text-sm font-bold text-gray-900">Save at {store.store_name} using GoerOne vouchers!</h3>
                                        <ol className="mt-3 space-y-2">
                                            {VOUCHER_STEPS.map((step, i) => (
                                                <li key={step} className="flex items-center gap-3 text-xs sm:text-sm text-gray-600">
                                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">
                                                        {i + 1}
                                                    </span>
                                                    {step}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}

                                {vouchers.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {vouchers.map((v) => {
                                            const qty = cart[v.id] || 0;
                                            const pricing = getVoucherPricing(v);
                                            const validity = getVoucherValidity(v);
                                            const termsOpenNow = Boolean(openTerms[v.id]);
                                            const { soldOut, remaining } = getVoucherAvailability(v);
                                            const eligible = isVoucherEligible(v, activeProgramId);
                                            const coins = getVoucherCoins(v);
                                            const redeem = redemptionLabel(v);
                                            const maxForCard = Math.min(MAX_VOUCHER_QTY, remaining);
                                            return (
                                                <div key={`voucher-${v.id}`} className={`relative flex h-full flex-col bg-emerald-50/60 border border-emerald-200/80 p-3 rounded-xl shadow-2xs ${soldOut ? "opacity-70" : ""}`}>
                                                    {soldOut && (
                                                        <span className="absolute right-2 top-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
                                                            Sold out
                                                        </span>
                                                    )}
                                                    <div className="flex items-center gap-1.5 text-emerald-900">
                                                        <Tag size={14} className="text-emerald-600 shrink-0" />
                                                        <span className="text-base font-extrabold leading-tight">{formatDiscount(v, "voucher")}</span>
                                                    </div>
                                                    <p className="mt-1.5 text-sm font-medium leading-snug text-gray-800 line-clamp-2">{v.voucher_name}</p>
                                                    {pricing.face > 0 && (
                                                        <p className="text-xs text-emerald-800 mt-1.5">
                                                            Worth {formatINR(pricing.face)} · Pay {formatINR(pricing.payable)}
                                                        </p>
                                                    )}
                                                    {pricing.isPercent && pricing.cap > 0 && (
                                                        <p className="text-xs text-emerald-700/80 mt-1">Max discount {formatINR(pricing.cap)}</p>
                                                    )}
                                                    {coins > 0 && (
                                                        <p className="text-xs text-emerald-700/80 mt-1">Requires {coins} GoerCoin</p>
                                                    )}
                                                    {redeem && <p className="text-xs text-emerald-700/80 mt-1">{redeem}</p>}
                                                    {validity.label && (
                                                        <p className={`text-xs mt-1 ${validity.usable ? "text-emerald-700/80" : "text-red-600"}`}>
                                                            {validity.label}
                                                        </p>
                                                    )}

                                                    {v.terms_condition && (
                                                        <div className="mt-2">
                                                            <button
                                                                type="button"
                                                                aria-expanded={termsOpenNow}
                                                                onClick={() => setOpenTerms((prev) => ({ ...prev, [v.id]: !prev[v.id] }))}
                                                                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
                                                            >
                                                                <Info size={12} /> T&amp;C
                                                            </button>
                                                            {termsOpenNow && (
                                                                <p className="mt-1 text-xs leading-relaxed text-gray-500">{v.terms_condition}</p>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div className="mt-auto flex justify-center pt-3">
                                                        {soldOut ? (
                                                            <button
                                                                type="button"
                                                                disabled
                                                                className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-gray-500 cursor-not-allowed"
                                                            >
                                                                Sold out
                                                            </button>
                                                        ) : !eligible ? (
                                                            <button
                                                                type="button"
                                                                onClick={handleUpgradeForVoucher}
                                                                className="inline-flex w-full items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-amber-400 bg-amber-50 text-xs font-bold leading-tight text-amber-800 hover:bg-amber-100 transition-colors"
                                                            >
                                                                <Crown size={14} className="shrink-0" /> Upgrade membership to get this voucher
                                                            </button>
                                                        ) : qty === 0 ? (
                                                            <button
                                                                type="button"
                                                                disabled={!validity.usable}
                                                                onClick={() => changeQty(v.id, 1)}
                                                                className="inline-flex w-full items-center justify-center gap-1 px-3.5 py-1.5 rounded-lg border border-[#0B67FC]/60 bg-white text-xs sm:text-sm font-bold text-[#0954D0] hover:bg-[#0B67FC]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                                            >
                                                                <Plus size={14} /> Add
                                                            </button>
                                                        ) : (
                                                            <QuantityStepper
                                                                qty={qty}
                                                                max={maxForCard}
                                                                onDec={() => changeQty(v.id, -1)}
                                                                onInc={() => changeQty(v.id, 1)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Proceed bar: appears once at least one voucher is added */}
            {cartCount > 0 && (
                <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                        <div>
                            <p className="text-xs text-gray-500">
                                {cartCount} voucher{cartCount > 1 ? "s" : ""} added
                            </p>
                            <p className="text-lg font-bold text-gray-900">{formatINR(cartTotal)}</p>
                        </div>
                        <button
                            type="button"
                            onClick={handleProceed}
                            className="px-8 py-3 rounded-xl bg-[#0B67FC] hover:bg-[#0954D0] text-white text-sm font-bold shadow-sm transition-all active:scale-[0.99]"
                        >
                            Proceed
                        </button>
                    </div>
                </div>
            )}

            <VoucherCheckoutPanel
                open={checkoutOpen}
                onClose={() => { setCheckoutOpen(false); setCartError(""); }}
                store={store}
                items={cartItems}
                onChangeQty={changeQty}
                onRemove={removeVoucher}
                onPay={handleVoucherPay}
                maxQty={MAX_VOUCHER_QTY}
                membership={membership}
                onChangeMembership={() => changeMembership("checkout")}
                issues={cartIssues}
                canCheckout={canCheckout}
                error={cartError}
            />

            <MembershipPage
                open={membershipOpen}
                onClose={() => setMembershipOpen(false)}
                store={store}
                rewards={rewards}
                ownedProgramIds={ownedProgramIds}
                selectedProgramId={selectedMembership?.program.program_id}
                currentProgramId={membershipStatus.programId}
                currentState={membershipStatus.state}
                currentAmount={membershipStatus.planAmount}
                credit={membershipCredit}
                isFirstMembership={membershipStatus.state === "none"}
                onSelect={handleMembershipSelect}
                onRequestConsideration={handleRequestConsideration}
                onHaveInvitation={handleHaveInvitation}
            />

           <PayBillPanel
                open={payBillOpen}
                onClose={() => setPayBillOpen(false)}
                store={store}
                rewards={payRewards}
                membership={membership}
                onChangeMembership={() => changeMembership("payBill")}
                onPay={handlePayBillSubmit}
            />

            <LoginRequiredModal open={loginOpen} onClose={() => setLoginOpen(false)} onLogin={handleLogin} />

            <OtpVerifyModal
                open={otpOpen}
                onClose={() => setOtpOpen(false)}
                phone={customerPhone}
                storeName={store.store_name}
                onSendOtp={sendPaymentOtp}
                onSubmit={handleOtpSubmit}
            />
        </div>
    );
}